/**
 * Host contract keys the Flutter appointment iframe reads from the `appointment`
 * object on every open (shared with the iOS / Android hosts).
 *
 * - `environment` is sent only when REACT_APP_ENV is a known value; when omitted
 *   the module falls back to its own build-time ENVIRONMENT dart-define.
 * - `isGatewayEnable` follows REACT_APP_APPOINTMENT_GATEWAY_ENABLED: "false" or
 *   "0" switches to the legacy Go API host, "true" or "1" keeps the gateway.
 *   Unset or empty uses DEFAULT_GATEWAY_ENABLED (true); any other value also
 *   uses the default and logs a warning so a typo does not fail silently.
 * - Never send `apiBaseUrl` or `gatewayHeaders` — the module picks its API host itself.
 */
const SUPPORTED_ENVIRONMENTS = ['prod', 'sdlc'];

// Gateway routing when REACT_APP_APPOINTMENT_GATEWAY_ENABLED is unset, empty or unrecognized.
export const DEFAULT_GATEWAY_ENABLED = true;
const GATEWAY_FLAG_VALUES = { true: true, 1: true, false: false, 0: false };

function readEnv(key) {
  return (process.env[key] || '').trim().toLowerCase();
}

export function getAppointmentEnvironment() {
  const value = readEnv('REACT_APP_ENV');
  return SUPPORTED_ENVIRONMENTS.includes(value) ? value : null;
}

export function isAppointmentGatewayEnabled() {
  const value = readEnv('REACT_APP_APPOINTMENT_GATEWAY_ENABLED');
  if (!value) return DEFAULT_GATEWAY_ENABLED;
  if (Object.prototype.hasOwnProperty.call(GATEWAY_FLAG_VALUES, value)) {
    return GATEWAY_FLAG_VALUES[value];
  }
  console.warn(
    `[appointmentHostContract] Unrecognized REACT_APP_APPOINTMENT_GATEWAY_ENABLED value "${value}"; ` +
      `expected true|false|1|0, using default (${DEFAULT_GATEWAY_ENABLED}).`
  );
  return DEFAULT_GATEWAY_ENABLED;
}

export function buildAppointmentHostContract() {
  const environment = getAppointmentEnvironment();
  return {
    streamKey: process.env.REACT_APP_STREAM_KEY || null, // Stream API key
    ...(environment && { environment }),
    isGatewayEnable: isAppointmentGatewayEnabled(),
  };
}
